/**
 * Proposal Drawer
 * Create / Edit proposals with financial details and payment schedule.
 */

import { Plus, Trash2, X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CurrencyInput, DatePicker, Input, NumberInput, TextArea } from '../forms';
import { useToast } from '../../contexts/ToastContext';
import {
  useCreateProposalMutation,
  useUpdateProposalMutation,
} from '../../services/proposalApi';
import type {
  Proposal,
  ClientContact,
  PaymentMilestone,
  CreateProposalRequest,
  UpdateProposalRequest,
} from '../../types/proposal';

// ============================================================================
// Props
// ============================================================================

interface ProposalDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  proposal?: Proposal | null;
  mode: 'create' | 'edit';
}

// ============================================================================
// Helpers
// ============================================================================

const buildEmptyMilestone = (): PaymentMilestone => ({
  name: '',
  description: '',
  percentage: 0,
  amount: 0,
  dueCondition: '',
});

const getTodayIso = (): string => new Date().toISOString().split('T')[0];

const getDefaultValidUntil = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().split('T')[0];
};

// ============================================================================
// Component
// ============================================================================

const ProposalDrawer: React.FC<ProposalDrawerProps> = ({
  isOpen,
  onClose,
  projectId,
  proposal,
  mode,
}) => {
  const toast = useToast();

  // --------------------------------------------------------------------------
  // Form state
  // --------------------------------------------------------------------------

  const [name, setName] = useState('');

  // Client contact
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');

  // Dates
  const [proposalDate, setProposalDate] = useState('');
  const [validUntil, setValidUntil] = useState('');

  // Content
  const [introduction, setIntroduction] = useState('');
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [inclusions, setInclusions] = useState<string[]>([]);
  const [exclusions, setExclusions] = useState<string[]>([]);
  const [assumptions, setAssumptions] = useState<string[]>([]);
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [warrantyTerms, setWarrantyTerms] = useState('');

  // Financial
  const [subtotal, setSubtotal] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [currency] = useState('USD');

  // Payment schedule
  const [paymentSchedule, setPaymentSchedule] = useState<PaymentMilestone[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [createProposal, { isLoading: isCreating }] = useCreateProposalMutation();
  const [updateProposal, { isLoading: isUpdating }] = useUpdateProposalMutation();

  const isSubmitting = isCreating || isUpdating;

  // --------------------------------------------------------------------------
  // Auto-calculate tax amount and total when subtotal/taxRate changes
  // --------------------------------------------------------------------------

  useEffect(() => {
    const taxAmt = subtotal * (taxRate / 100);
    setTotalAmount(subtotal + taxAmt);
  }, [subtotal, taxRate]);

  // --------------------------------------------------------------------------
  // Reset / populate form
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (isOpen && mode === 'edit' && proposal) {
      setName(proposal.name || '');
      setClientName(proposal.clientContact?.name || '');
      setClientEmail(proposal.clientContact?.email || '');
      setClientPhone(proposal.clientContact?.phone || '');
      setProposalDate(proposal.proposalDate ? proposal.proposalDate.split('T')[0] : '');
      setValidUntil(proposal.validUntil ? proposal.validUntil.split('T')[0] : '');
      setIntroduction(proposal.introduction || '');
      setScopeOfWork(proposal.scopeOfWork || '');
      setInclusions(proposal.inclusions || []);
      setExclusions(proposal.exclusions || []);
      setAssumptions(proposal.assumptions || []);
      setTermsAndConditions(proposal.termsAndConditions || '');
      setWarrantyTerms(proposal.warrantyTerms || '');
      setSubtotal(proposal.subtotal || 0);
      setTaxRate(proposal.taxRate ?? 0);
      setTotalAmount(proposal.totalAmount || 0);
      setPaymentSchedule(
        (proposal.paymentSchedule || []).map(m => ({
          name: m.name,
          description: m.description || '',
          percentage: m.percentage,
          amount: m.amount,
          dueCondition: m.dueCondition,
        }))
      );
      setErrors({});
    } else if (isOpen && mode === 'create') {
      setName('');
      setClientName('');
      setClientEmail('');
      setClientPhone('');
      setProposalDate(getTodayIso());
      setValidUntil(getDefaultValidUntil());
      setIntroduction('');
      setScopeOfWork('');
      setInclusions([]);
      setExclusions([]);
      setAssumptions([]);
      setTermsAndConditions('');
      setWarrantyTerms('');
      setSubtotal(0);
      setTaxRate(0);
      setTotalAmount(0);
      setPaymentSchedule([]);
      setErrors({});
    }
  }, [isOpen, mode, proposal]);

  // --------------------------------------------------------------------------
  // Validation
  // --------------------------------------------------------------------------

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Proposal name is required';
    if (!clientName.trim()) errs.clientName = 'Client name is required';
    if (!clientEmail.trim()) errs.clientEmail = 'Client email is required';
    if (!proposalDate) errs.proposalDate = 'Proposal date is required';
    if (!validUntil) errs.validUntil = 'Valid until date is required';
    if (!introduction.trim()) errs.introduction = 'Introduction is required';
    if (!scopeOfWork.trim()) errs.scopeOfWork = 'Scope of work is required';
    if (!termsAndConditions.trim()) errs.termsAndConditions = 'Terms and conditions are required';
    if (subtotal < 0) errs.subtotal = 'Subtotal cannot be negative';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // --------------------------------------------------------------------------
  // Payment schedule management
  // --------------------------------------------------------------------------

  const handleAddMilestone = useCallback(() => {
    setPaymentSchedule(prev => [...prev, buildEmptyMilestone()]);
  }, []);

  const handleRemoveMilestone = useCallback((index: number) => {
    setPaymentSchedule(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleMilestoneChange = useCallback(
    (index: number, field: keyof PaymentMilestone, value: string | number) => {
      setPaymentSchedule(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    },
    []
  );

  // --------------------------------------------------------------------------
  // List field helpers (inclusions / exclusions / assumptions)
  // --------------------------------------------------------------------------

  const handleListItemChange = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    index: number,
    value: string
  ) => {
    setter(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleAddListItem = (setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter(prev => [...prev, '']);
  };

  const handleRemoveListItem = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    index: number
  ) => {
    setter(prev => prev.filter((_, i) => i !== index));
  };

  // --------------------------------------------------------------------------
  // Submit
  // --------------------------------------------------------------------------

  const handleSubmit = async () => {
    if (!validate()) return;

    const clientContact: ClientContact = {
      name: clientName.trim(),
      email: clientEmail.trim(),
      phone: clientPhone.trim() || undefined,
    };

    const taxAmt = subtotal * (taxRate / 100);

    const payload: CreateProposalRequest | UpdateProposalRequest = {
      name: name.trim(),
      clientContact,
      proposalDate,
      validUntil,
      introduction: introduction.trim(),
      scopeOfWork: scopeOfWork.trim(),
      inclusions: inclusions.filter(i => i.trim() !== ''),
      exclusions: exclusions.filter(e => e.trim() !== ''),
      assumptions: assumptions.filter(a => a.trim() !== ''),
      termsAndConditions: termsAndConditions.trim(),
      warrantyTerms: warrantyTerms.trim() || undefined,
      subtotal,
      taxRate,
      taxAmount: taxAmt,
      totalAmount,
      currency,
      paymentSchedule: paymentSchedule.filter(m => m.name.trim() !== ''),
    };

    try {
      if (mode === 'create') {
        await createProposal({
          projectId,
          data: payload as CreateProposalRequest,
        }).unwrap();
        toast.success('Proposal created successfully');
      } else if (proposal) {
        await updateProposal({
          projectId,
          proposalId: proposal._id,
          data: payload as UpdateProposalRequest,
        }).unwrap();
        toast.success('Proposal updated successfully');
      }
      onClose();
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'data' in err &&
        typeof (err as { data?: { message?: string } }).data?.message === 'string'
          ? (err as { data: { message: string } }).data.message
          : mode === 'create'
          ? 'Failed to create proposal'
          : 'Failed to update proposal';
      toast.error(message);
    }
  };

  if (!isOpen) return null;

  const paymentPercentageTotal = paymentSchedule.reduce((sum, m) => sum + (m.percentage || 0), 0);
  const percentageValid = paymentSchedule.length === 0 || paymentPercentageTotal === 100;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Drawer — large because it has multiple sections */}
      <div className="fixed inset-6 z-[100] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {mode === 'create' ? 'Create Proposal' : 'Edit Proposal'}
              </h2>
              {proposal && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {proposal.proposalNumber} &mdash; Version {proposal.version}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <div className="space-y-8">

              {/* ==============================================================
                  SECTION: Proposal Details
              ============================================================== */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Proposal Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">
                      Proposal Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="e.g. Kitchen Renovation Proposal"
                    />
                    {errors.name && (
                      <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Proposal Date <span className="text-red-500">*</span>
                    </label>
                    <DatePicker
                      value={proposalDate}
                      onChange={setProposalDate}
                      placeholder="Select date"
                    />
                    {errors.proposalDate && (
                      <p className="text-xs text-red-500 mt-1">{errors.proposalDate}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Valid Until <span className="text-red-500">*</span>
                    </label>
                    <DatePicker
                      value={validUntil}
                      onChange={setValidUntil}
                      placeholder="Select date"
                    />
                    {errors.validUntil && (
                      <p className="text-xs text-red-500 mt-1">{errors.validUntil}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* ==============================================================
                  SECTION: Client Contact
              ============================================================== */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Client Contact
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Client Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={clientName}
                      onChange={e => setClientName(e.target.value)}
                      placeholder="Full name"
                    />
                    {errors.clientName && (
                      <p className="text-xs text-red-500 mt-1">{errors.clientName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Client Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={clientEmail}
                      onChange={e => setClientEmail(e.target.value)}
                      placeholder="client@example.com"
                      type="email"
                    />
                    {errors.clientEmail && (
                      <p className="text-xs text-red-500 mt-1">{errors.clientEmail}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Phone</label>
                    <Input
                      value={clientPhone}
                      onChange={e => setClientPhone(e.target.value)}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>
              </div>

              {/* ==============================================================
                  SECTION: Content
              ============================================================== */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Proposal Content
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Introduction <span className="text-red-500">*</span>
                    </label>
                    <TextArea
                      value={introduction}
                      onChange={setIntroduction}
                      placeholder="We are pleased to submit the following proposal..."
                      rows={3}
                    />
                    {errors.introduction && (
                      <p className="text-xs text-red-500 mt-1">{errors.introduction}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Scope of Work <span className="text-red-500">*</span>
                    </label>
                    <TextArea
                      value={scopeOfWork}
                      onChange={setScopeOfWork}
                      placeholder="Describe the complete scope of work..."
                      rows={4}
                    />
                    {errors.scopeOfWork && (
                      <p className="text-xs text-red-500 mt-1">{errors.scopeOfWork}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* ==============================================================
                  SECTION: Inclusions / Exclusions / Assumptions
              ============================================================== */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Inclusions, Exclusions & Assumptions
                </h3>
                <div className="grid grid-cols-3 gap-6">
                  {/* Inclusions */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-600">Inclusions</label>
                      <button
                        type="button"
                        onClick={() => handleAddListItem(setInclusions)}
                        className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        Add
                      </button>
                    </div>
                    <div className="space-y-2">
                      {inclusions.length === 0 && (
                        <p className="text-xs text-gray-400 italic">None added</p>
                      )}
                      {inclusions.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-1">
                          <Input
                            value={item}
                            onChange={e =>
                              handleListItemChange(setInclusions, idx, e.target.value)
                            }
                            placeholder="e.g. All labor and materials"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveListItem(setInclusions, idx)}
                            className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Exclusions */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-600">Exclusions</label>
                      <button
                        type="button"
                        onClick={() => handleAddListItem(setExclusions)}
                        className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        Add
                      </button>
                    </div>
                    <div className="space-y-2">
                      {exclusions.length === 0 && (
                        <p className="text-xs text-gray-400 italic">None added</p>
                      )}
                      {exclusions.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-1">
                          <Input
                            value={item}
                            onChange={e =>
                              handleListItemChange(setExclusions, idx, e.target.value)
                            }
                            placeholder="e.g. Furniture"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveListItem(setExclusions, idx)}
                            className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Assumptions */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-600">Assumptions</label>
                      <button
                        type="button"
                        onClick={() => handleAddListItem(setAssumptions)}
                        className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        Add
                      </button>
                    </div>
                    <div className="space-y-2">
                      {assumptions.length === 0 && (
                        <p className="text-xs text-gray-400 italic">None added</p>
                      )}
                      {assumptions.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-1">
                          <Input
                            value={item}
                            onChange={e =>
                              handleListItemChange(setAssumptions, idx, e.target.value)
                            }
                            placeholder="e.g. Site access available"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveListItem(setAssumptions, idx)}
                            className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* ==============================================================
                  SECTION: Financial Summary
              ============================================================== */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Financial Summary
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Subtotal ({currency}) <span className="text-red-500">*</span>
                    </label>
                    <CurrencyInput
                      value={subtotal}
                      onChange={setSubtotal}
                      placeholder="0.00"
                    />
                    {errors.subtotal && (
                      <p className="text-xs text-red-500 mt-1">{errors.subtotal}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Tax Rate (%)</label>
                    <NumberInput
                      value={taxRate}
                      onChange={setTaxRate}
                      placeholder="0"
                      min={0}
                      max={100}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Total Amount ({currency})
                    </label>
                    <CurrencyInput
                      value={totalAmount}
                      onChange={setTotalAmount}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              {/* ==============================================================
                  SECTION: Payment Schedule
              ============================================================== */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Payment Schedule
                    </h3>
                    {paymentSchedule.length > 0 && (
                      <p
                        className={`text-xs mt-0.5 ${
                          percentageValid ? 'text-green-600' : 'text-red-500'
                        }`}
                      >
                        {paymentPercentageTotal}% allocated
                        {!percentageValid && ' — must total 100%'}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleAddMilestone}
                    className="flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Add Milestone
                  </button>
                </div>

                {paymentSchedule.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <p className="text-sm text-gray-500">No payment milestones yet.</p>
                    <button
                      type="button"
                      onClick={handleAddMilestone}
                      className="mt-2 text-sm text-brand-600 hover:underline"
                    >
                      Add first milestone
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paymentSchedule.map((milestone, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-xl p-4 bg-gray-50"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-1 grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-0.5">
                                Milestone Name
                              </label>
                              <Input
                                value={milestone.name}
                                onChange={e =>
                                  handleMilestoneChange(index, 'name', e.target.value)
                                }
                                placeholder="e.g. Deposit"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-0.5">
                                Due Condition
                              </label>
                              <Input
                                value={milestone.dueCondition}
                                onChange={e =>
                                  handleMilestoneChange(index, 'dueCondition', e.target.value)
                                }
                                placeholder="e.g. Upon contract signing"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-0.5">
                                Percentage (%)
                              </label>
                              <NumberInput
                                value={milestone.percentage}
                                onChange={val => handleMilestoneChange(index, 'percentage', val)}
                                placeholder="0"
                                min={0}
                                max={100}
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-0.5">
                                Amount ({currency})
                              </label>
                              <CurrencyInput
                                value={milestone.amount}
                                onChange={val => handleMilestoneChange(index, 'amount', val)}
                                placeholder="0.00"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-0.5">
                                Description (optional)
                              </label>
                              <Input
                                value={milestone.description || ''}
                                onChange={e =>
                                  handleMilestoneChange(index, 'description', e.target.value)
                                }
                                placeholder="Additional details"
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveMilestone(index)}
                            className="flex-shrink-0 p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors mt-1"
                            aria-label="Remove milestone"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ==============================================================
                  SECTION: Terms & Warranty
              ============================================================== */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Terms & Conditions
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Terms & Conditions <span className="text-red-500">*</span>
                    </label>
                    <TextArea
                      value={termsAndConditions}
                      onChange={setTermsAndConditions}
                      placeholder="Standard terms and conditions..."
                      rows={4}
                    />
                    {errors.termsAndConditions && (
                      <p className="text-xs text-red-500 mt-1">{errors.termsAndConditions}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Warranty Terms (optional)
                    </label>
                    <TextArea
                      value={warrantyTerms}
                      onChange={setWarrantyTerms}
                      placeholder="Describe warranty coverage..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 flex items-center justify-between">
            <div className="text-xs text-gray-400">
              {paymentSchedule.length > 0 && !percentageValid && (
                <span className="text-red-500">Payment schedule must total 100%</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting
                  ? mode === 'create'
                    ? 'Creating...'
                    : 'Saving...'
                  : mode === 'create'
                  ? 'Create Proposal'
                  : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ProposalDrawer;
